import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Upload, X, ArrowLeft, ArrowRight, Search, ImageIcon, Check } from "lucide-react";
import { api, assetUrl } from "../../adminApi";
import { Card, SectionTitle } from "../../components/admin/AdminUI";

export default function AdminMedia() {
  const [projects, setProjects] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const input = useRef(null);

  const load = () =>
    api.projects().then((list) => {
      setProjects(list);
      setActiveId((cur) => cur || list[0]?.id || null);
    });

  useEffect(() => {
    load();
  }, []);

  const active = useMemo(() => (projects || []).find((p) => p.id === activeId), [projects, activeId]);

  const filtered = useMemo(
    () =>
      (projects || []).filter(
        (p) =>
          !q ||
          (p.title?.en || "").toLowerCase().includes(q.toLowerCase()) ||
          (p.category || "").toLowerCase().includes(q.toLowerCase())
      ),
    [projects, q]
  );

  const persist = async (images) => {
    if (!active) return;
    const body = {
      slug: active.slug,
      title: active.title,
      description: active.description,
      category: active.category,
      location: active.location,
      year: active.year,
      client: active.client,
      scope: active.scope,
      images,
      featured: active.featured,
      order: active.order,
    };
    const saved = await api.updateProject(active.id, body);
    setProjects((list) => list.map((p) => (p.id === saved.id ? saved : p)));
  };

  const handleFiles = async (fileList) => {
    if (!fileList || !fileList.length || !active) return;
    setErr("");
    setMsg("");
    setBusy(true);
    try {
      const res = await api.upload(fileList);
      await persist([...(active.images || []), ...res.files.map((f) => f.url)]);
      setMsg(`${res.files.length} görsel "${active.title?.en}" projesine eklendi.`);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };

  const removeAt = (i) => persist((active.images || []).filter((_, k) => k !== i));
  const moveAt = (i, d) => {
    const next = [...(active.images || [])];
    const j = i + d;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    persist(next);
  };

  return (
    <div data-testid="admin-media">
      <SectionTitle label="[ TOPLU GÖRSEL YÜKLEME ]" title="Proje görselleri" />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* project picker */}
        <Card className="lg:col-span-4 p-0 overflow-hidden self-start">
          <div className="p-4 border-b border-[#E2E0D8] relative">
            <Search size={13} className="absolute left-7 top-1/2 -translate-y-1/2 text-[#A8A29A]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Proje ara..."
              data-testid="media-search"
              className="w-full bg-[#FAFAFA] border border-[#E2E0D8] pl-9 pr-3 py-2.5 text-[13.5px] outline-none focus:border-[#C8B29B] transition-colors"
            />
          </div>
          <div className="max-h-[560px] overflow-y-auto">
            {!projects ? (
              <div className="p-6">
                <Loader2 size={16} className="animate-spin text-[#8C7862]" />
              </div>
            ) : (
              filtered.map((p) => (
                <button
                  key={p.id}
                  onClick={() => {
                    setActiveId(p.id);
                    setMsg("");
                    setErr("");
                  }}
                  data-testid={`media-project-${p.slug}`}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left border-b border-[#E2E0D8] last:border-b-0 transition-colors ${
                    p.id === activeId ? "bg-[#141414]" : "hover:bg-[#FAFAFA]"
                  }`}
                >
                  <div className="w-[46px] h-[34px] bg-[#F2F0EB] border border-[#E2E0D8] overflow-hidden shrink-0">
                    {p.images?.[0] && <img src={assetUrl(p.images[0])} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`text-[13.5px] truncate ${p.id === activeId ? "text-[#FAFAFA]" : "text-[#141414]"}`}>
                      {p.title?.en || p.slug}
                    </p>
                    <p className={`mono text-[8px] mt-0.5 ${p.id === activeId ? "text-[#C8B29B]" : "text-[#A8A29A]"}`}>
                      {p.category} · {(p.images || []).length} GÖRSEL
                    </p>
                  </div>
                  {p.id === activeId && <Check size={13} className="text-[#C8B29B] shrink-0" />}
                </button>
              ))
            )}
          </div>
        </Card>

        {/* dropzone + gallery */}
        <div className="lg:col-span-8">
          {!active ? (
            <Card className="p-10">
              <p className="mono text-[9.5px] text-[#6B675F]">SOLDAN BİR PROJE SEÇİN</p>
            </Card>
          ) : (
            <Card className="p-6 lg:p-8">
              <p className="mono text-[9px] text-[#8C7862] mb-2">SEÇİLİ PROJE</p>
              <h2 className="display text-[26px] lg:text-[32px] text-[#141414] leading-tight">
                {active.title?.en || active.slug}
              </h2>
              <p className="mono text-[8.5px] text-[#A8A29A] mt-2">
                {active.category} · {active.location} · {active.year}
              </p>

              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setDrag(true);
                }}
                onDragLeave={() => setDrag(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDrag(false);
                  handleFiles(e.dataTransfer.files);
                }}
                onClick={() => input.current?.click()}
                data-testid="media-dropzone"
                className={`mt-7 border-2 border-dashed cursor-pointer py-14 text-center transition-colors duration-300 ${
                  drag ? "border-[#8C7862] bg-[#F2EFE8]" : "border-[#E2E0D8] bg-[#FAFAFA] hover:border-[#C8B29B]"
                }`}
              >
                {busy ? (
                  <Loader2 size={22} className="animate-spin text-[#8C7862] mx-auto" />
                ) : (
                  <Upload size={22} strokeWidth={1.4} className="text-[#8C7862] mx-auto" />
                )}
                <p className="text-[15px] text-[#141414] mt-4">
                  {busy ? "Görseller yükleniyor..." : "Görselleri buraya sürükleyip bırakın"}
                </p>
                <p className="mono text-[8.5px] text-[#A8A29A] mt-2">
                  VEYA TIKLAYIP SEÇİN · JPG / PNG / WEBP · MAKS. 25 MB
                </p>
              </div>
              <input
                ref={input}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => handleFiles(e.target.files)}
                className="hidden"
                data-testid="media-input"
              />

              {msg && <p className="mono text-[9px] text-[#8C7862] mt-4">{msg}</p>}
              {err && <p className="text-[13px] text-[#8C2E1D] mt-4">{err}</p>}

              <div className="mt-8">
                <p className="mono text-[9px] text-[#6B675F] mb-3">
                  MEVCUT GÖRSELLER ({(active.images || []).length})
                </p>
                {(active.images || []).length === 0 ? (
                  <div className="border border-dashed border-[#E2E0D8] py-10 text-center">
                    <ImageIcon size={18} strokeWidth={1.4} className="text-[#A8A29A] mx-auto mb-3" />
                    <p className="mono text-[9px] text-[#A8A29A]">HENÜZ GÖRSEL YOK</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {(active.images || []).map((src, i) => (
                      <div key={`${src}-${i}`} className="relative group border border-[#E2E0D8] bg-[#FAFAFA]">
                        <img src={assetUrl(src)} alt="" className="w-full h-[104px] object-cover" />
                        <span className="absolute top-1.5 left-1.5 mono text-[8px] bg-white/90 px-1.5 py-0.5">
                          {i === 0 ? "KAPAK" : String(i + 1).padStart(2, "0")}
                        </span>
                        <div className="absolute inset-x-0 bottom-0 flex opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => moveAt(i, -1)} className="flex-1 bg-white/95 py-1.5 flex justify-center">
                            <ArrowLeft size={12} />
                          </button>
                          <button
                            onClick={() => moveAt(i, 1)}
                            className="flex-1 bg-white/95 py-1.5 flex justify-center border-x border-[#E2E0D8]"
                          >
                            <ArrowRight size={12} />
                          </button>
                          <button
                            onClick={() => removeAt(i)}
                            data-testid={`media-remove-${i}`}
                            className="flex-1 bg-white/95 py-1.5 flex justify-center text-[#8C2E1D]"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
